import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, startOfWeek, subDays } from 'date-fns';
import { CalendarClock, ChevronRight, Pencil, Plus } from 'lucide-react';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { StatusBadge } from '../../components/StatusBadge';
import { useDb } from '../../db/hooks';
import { useProfile } from '../../data/profile';
import { useSchedules } from '../../data/schedule';
import { markMissed, useSessions, useSessionVitalsSummary } from '../../data/sessions';
import { useSettings } from '../../data/settings';
import { useCanWrite } from '../../account/useCanWrite';
import { formatKg, interdialyticGains, STATUS_LABELS, unloggedPlanned, weightRemoved, type SessionRow } from '../../lib/sessions';
import { plannedSessions, scheduleOn, type PlannedSession } from '../../lib/schedule';
import { ScheduleForm } from '../schedule/ScheduleForm';
import { ScheduleChanges } from '../schedule/ScheduleChanges';
import { describeSchedule } from '../schedule/describe';
import { localToday } from './fields';
import { STATUS_TONES } from './SessionPage';

function NotLoggedItem({ planned, onMissed }: { planned: PlannedSession; onMissed: (reason: string | null) => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const label = format(parseISO(planned.date), 'EEEE d MMM');
  return (
    <li className="space-y-2 py-2.5">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm font-medium">{label}</span>
        <Link to={`/sessions/new?date=${planned.date}`} className="focus-ring rounded-lg px-3 py-1.5 text-sm font-semibold text-clinical-primary hover:bg-clinical-primary/10">
          Log it
        </Link>
        <button
          type="button"
          onClick={() => setAsking(true)}
          className="focus-ring rounded-lg px-3 py-1.5 text-sm font-semibold text-clinical-muted hover:bg-clinical-surface"
          aria-label={`Mark ${label} as missed`}
        >
          Missed
        </button>
      </div>
      {asking ? (
        <div className="flex gap-2">
          <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason (optional)" aria-label="Reason for missing" />
          <Button onClick={() => void onMissed(reason.trim() || null)}>Save</Button>
        </div>
      ) : null}
    </li>
  );
}

function SessionItem({
  session,
  gain,
  bp
}: {
  session: SessionRow;
  gain: number | undefined;
  bp: { pre?: { bp_systolic: number | null; bp_diastolic: number | null }; post?: { bp_systolic: number | null; bp_diastolic: number | null } } | undefined;
}) {
  const removed = weightRemoved(session);
  const fmtBp = (vital?: { bp_systolic: number | null; bp_diastolic: number | null }) =>
    vital?.bp_systolic != null ? `${vital.bp_systolic}/${vital.bp_diastolic}` : null;
  const bpText = [fmtBp(bp?.pre), fmtBp(bp?.post)].filter(Boolean).join(' → ');
  return (
    <li>
      <Link to={`/sessions/${session.id}`} className="focus-ring flex items-center gap-3 px-4 py-3 hover:bg-clinical-surface">
        <div className="w-14 shrink-0 text-center">
          <p className="text-xs uppercase text-clinical-muted">{format(parseISO(session.session_date), 'EEE')}</p>
          <p className="text-lg font-semibold">{format(parseISO(session.session_date), 'd')}</p>
        </div>
        <div className="min-w-0 flex-1 text-sm">
          {session.status === 'missed' ? (
            <p className="text-clinical-muted">{session.missed_reason ?? 'No session'}</p>
          ) : (
            <>
              <p className="font-medium">
                {session.pre_weight_kg != null ? `${session.pre_weight_kg} → ${session.post_weight_kg ?? '…'} kg` : 'No weights yet'}
                {removed != null ? <span className="text-clinical-muted"> ({formatKg(-removed, true)})</span> : null}
              </p>
              <p className="text-clinical-muted">
                {[gain != null ? `gain ${formatKg(gain, true)}` : null, bpText ? `BP ${bpText}` : null].filter(Boolean).join(' · ') || ' '}
              </p>
            </>
          )}
          {session.status !== 'completed' ? (
            <span className="mt-1 inline-block">
              <StatusBadge tone={STATUS_TONES[session.status]}>{STATUS_LABELS[session.status]}</StatusBadge>
            </span>
          ) : null}
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-clinical-muted" />
      </Link>
    </li>
  );
}

export function SessionsPage() {
  const db = useDb();
  const canWrite = useCanWrite();
  const { current, schedules, exceptions, isLoading } = useSchedules();
  const { profile } = useProfile();
  const { sessions } = useSessions();
  const vitals = useSessionVitalsSummary();
  const { settings } = useSettings();
  const [editing, setEditing] = useState(false);

  const today = localToday();
  const gains = interdialyticGains(sessions);
  const notLogged = isLoading
    ? []
    : unloggedPlanned(plannedSessions(schedules, exceptions, subDays(new Date(), 14), subDays(new Date(), 1)), sessions, today).reverse();

  const weekStartsOn = settings.week_starts_on === 'sun' ? 0 : 1;
  const weeks: { key: string; label: string; items: SessionRow[] }[] = [];
  for (const session of sessions) {
    const start = startOfWeek(parseISO(session.session_date), { weekStartsOn });
    const key = format(start, 'yyyy-MM-dd');
    let week = weeks.find((entry) => entry.key === key);
    if (!week) {
      week = { key, label: `Week of ${format(start, 'd MMM yyyy')}`, items: [] };
      weeks.push(week);
    }
    week.items.push(session);
  }

  return (
    <Page
      title="Sessions"
      actions={
        canWrite ? (
          <Link
            to="/sessions/new"
            className="focus-ring inline-flex min-h-9 items-center gap-1 rounded-lg bg-clinical-primary px-3 text-sm font-semibold text-white hover:bg-clinical-primary-hover"
          >
            <Plus className="h-4 w-4" /> Log
          </Link>
        ) : null
      }
    >
      {notLogged.length && canWrite ? (
        <Card title="Not logged yet">
          <ul className="divide-y divide-clinical-line">
            {notLogged.map((planned) => (
              <NotLoggedItem
                key={planned.date}
                planned={planned}
                onMissed={async (reason) => {
                  await markMissed(db, planned.date, scheduleOn(schedules, planned.date)?.modality ?? profile?.modality ?? 'in_center', reason);
                }}
              />
            ))}
          </ul>
        </Card>
      ) : null}

      {weeks.length ? (
        weeks.map((week) => (
          <section key={week.key}>
            <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-clinical-muted">{week.label}</h2>
            <ul className="divide-y divide-clinical-line overflow-hidden rounded-xl border border-clinical-line bg-clinical-card">
              {week.items.map((session) => (
                <SessionItem key={session.id} session={session} gain={gains.get(session.id)} bp={vitals.get(session.id)} />
              ))}
            </ul>
          </section>
        ))
      ) : (
        <EmptyState
          title="No sessions logged yet"
          body="Log weight and blood pressure before and after each session to see your trends."
          action={
            canWrite ? (
              <Link to="/sessions/new" className="font-semibold text-clinical-primary hover:underline">
                Log your first session
              </Link>
            ) : undefined
          }
        />
      )}

      <Card title="Your schedule">
        {isLoading ? null : editing ? (
          <ScheduleForm
            current={current}
            modality={profile?.modality ?? null}
            onSaved={() => setEditing(false)}
            secondary={
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            }
          />
        ) : current ? (
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-clinical-primary" />
            <div className="flex-1 text-sm">
              <p className="font-medium">{describeSchedule(current).days}</p>
              <p className="text-clinical-muted">{describeSchedule(current).details}</p>
            </div>
            {canWrite ? (
              <Button variant="secondary" onClick={() => setEditing(true)} aria-label="Edit schedule">
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-clinical-muted">No schedule yet. Add your usual dialysis days to see your next session on Today.</p>
            {canWrite ? <Button onClick={() => setEditing(true)}>Add schedule</Button> : null}
          </div>
        )}
      </Card>

      {current ? (
        <Card title="Changes to your schedule">
          <ScheduleChanges exceptions={exceptions} canWrite={canWrite} />
        </Card>
      ) : null}
    </Page>
  );
}
